import React, { useState, useEffect, useRef, useCallback } from 'react';
import Button from './button';
import Icon from '../appIcon';
import axiosInstance from 'utils/axiosInstance';

const DirectVideoStream = ({ cameraId, cameraType, cameraName }) => {
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [captureStatus, setCaptureStatus] = useState('idle'); // idle, capturing, success, error
  const [streamLoading, setStreamLoading] = useState(false);
  const [streamUrl, setStreamUrl] = useState('');
  const [togglingStop, setTogglingStop] = useState(false);
  const containerRef = useRef(null);

  // Set mounted state when component mounts
  useEffect(() => {
    fetchCameras();

    return () => {
      // Clean up when component unmounts
    };
  }, []);

  // Fetch cameras from the API
  const fetchCameras = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/camera-settings/getall');
      // Filter only active cameras
      const activeCameras = response.data.filter(camera => camera.is_active);
      setCameras(activeCameras);

      // If cameraId is provided as a prop, select that camera if it's active
      if (cameraId) {
        const camera = activeCameras.find(cam => cam.camera_id === cameraId);
        if (camera) {
          setSelectedCamera(camera);
          // Set the stream URL immediately
          const baseUrl = `${import.meta.env.VITE_API_URL}cctv/stream/video`;
          const url = `${baseUrl}/${camera.camera_id}`;
          setStreamUrl(url);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch cameras:", err);
      setError("Failed to load cameras. Please try again.");
      setLoading(false);
    }
  };

  // Update stream URL when selected camera changes
  useEffect(() => {
    if (selectedCamera) {
      // setStreamLoading(true);
      setError(null);
      const baseUrl = `${import.meta.env.VITE_API_URL}cctv/stream/video`;
      const url = `${baseUrl}/${selectedCamera.camera_id}`;
      console.log("Setting stream URL:", url);
      setStreamUrl(url);
    }
  }, [selectedCamera]);

  // Toggle camera stop status
  const toggleCameraStop = async () => {
    if (!selectedCamera) return;

    try {
      setTogglingStop(true);
      const response = await axiosInstance.put(`/camera-settings/toggle-stop/${selectedCamera.camera_id}`);

      // Update the selected camera with the new isStop status
      setSelectedCamera(prev => ({
        ...prev,
        isStop: response.data.isStop
      }));

      // Update the camera in the cameras list as well
      setCameras(prev => prev.map(cam =>
        cam.camera_id === selectedCamera.camera_id
          ? { ...cam, isStop: response.data.isStop }
          : cam
      ));

      // Show success message
      console.log(response.data.message);
    } catch (err) {
      console.error("Failed to toggle camera stop status:", err);
      setError("Failed to toggle camera status. Please try again.");
    } finally {
      setTogglingStop(false);
    }
  };

  const handleIframeLoad = () => {
    console.log("Iframe loaded successfully");
    setStreamLoading(false);
    setError(null);
    setCaptureStatus('capturing'); // Face capture is happening in background
  };

  const handleIframeError = () => {
    console.error("Iframe error");
    setError("Failed to load video stream. Please check camera connection.");
    setStreamLoading(false);
    setCaptureStatus('error');
  };

  const handleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(err => {
        setError(`Error attempting to enable fullscreen: ${err.message}`);
      });
      setFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setFullscreen(false);
      }
    }
  };

  const handleCameraSelect = (camera) => {
    setSelectedCamera(camera);
    // setStreamLoading(true);
    setError(null);
  };

  const handleBackToCameras = () => {
    setSelectedCamera(null);
    // setStreamLoading(true);
    setError(null);
    setStreamUrl('');
  };

  const refreshStream = () => {
    if (selectedCamera) {
      // setStreamLoading(true);
      setError(null);
      const baseUrl = `${import.meta.env.VITE_API_URL}cctv/stream/video`;
      const url = `${baseUrl}/${selectedCamera.camera_id}?t=${new Date().getTime()}`;
      console.log("Refreshing stream URL:", url);
      setStreamUrl(url);
    }
  };

  const getCaptureStatusIcon = () => {
    switch (captureStatus) {
      case 'capturing':
        return <Icon name="UserCheck" size={16} className="text-green-500" />;
      case 'success':
        return <Icon name="CheckCircle" size={16} className="text-green-500" />;
      case 'error':
        return <Icon name="AlertTriangle" size={16} className="text-red-500" />;
      default:
        return <Icon name="User" size={16} className="text-gray-500" />;
    }
  };

  const getCaptureStatusText = () => {
    switch (captureStatus) {
      case 'capturing':
        return 'Capturing faces...';
      case 'success':
        return 'Faces captured';
      case 'error':
        return 'Capture failed';
      default:
        return 'Idle';
    }
  };

  const getCameraTypeDisplay = (cameraType) => {
    switch (cameraType) {
      case "check_in":
        return "Check-in Only";
      case "check_out":
        return "Check-out Only";
      case "both":
        return "Check-in & Check-out";
      default:
        return "Unknown";
    }
  };

  const getCameraTypeColor = (cameraType) => {
    switch (cameraType) {
      case "check_in":
        return "bg-green-100 text-green-800";
      case "check_out":
        return "bg-blue-100 text-blue-800";
      case "both":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Group cameras by type (only active cameras)
  const checkInCameras = cameras.filter(cam => cam.camera_type === "check_in" || cam.camera_type === "both");
  const checkOutCameras = cameras.filter(cam => cam.camera_type === "check_out" || cam.camera_type === "both");

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden p-8">
        <div className="flex justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-gray-600">Loading cameras...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !selectedCamera) {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden p-8">
        <div className="text-center">
          <Icon name="AlertTriangle" size={48} className="text-red-500 mx-auto mb-2" />
          <p className="text-red-500 font-medium">Error</p>
          <p className="text-gray-600 mt-1">{error}</p>
          <Button onClick={fetchCameras} className="mt-3">Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {selectedCamera ? (
        <>
          <div className="p-4 border-b flex justify-between items-center">
            <div className="flex items-center">
              <Button variant="outline" onClick={handleBackToCameras} className="mr-3">
                <Icon name="ArrowLeft" size={16} />
              </Button>
              <h3 className="text-lg font-semibold">
                {selectedCamera.name}
              </h3>
              <span className={`ml-3 text-sm px-2 py-1 rounded-full ${getCameraTypeColor(selectedCamera.camera_type)}`}>
                {getCameraTypeDisplay(selectedCamera.camera_type)}
              </span>
              <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Active
              </span>
              {selectedCamera.isStop && (
                <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Capture Stopped
                </span>
              )}
            </div>
            <div className="flex space-x-2">
              <div className="flex items-center text-sm">
                {getCaptureStatusIcon()}
                <span className="ml-1">{getCaptureStatusText()}</span>
              </div>
              <Button
                variant={selectedCamera.isStop ? "default" : "outline"}
                onClick={toggleCameraStop}
                disabled={togglingStop}
                className={selectedCamera.isStop ? "bg-green-600 hover:bg-green-700" : ""}
              >
                {togglingStop ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></div>
                ) : (
                  selectedCamera.isStop ? <Icon name="Play" size={16} className="mr-1" /> : <Icon name="Square" size={16} className="mr-1" />
                )}
                {selectedCamera.isStop ? "Start Capture" : "Stop Capture"}
              </Button>
              <Button variant="outline" onClick={refreshStream}>
                <Icon name="RefreshCw" size={16} />
              </Button>
              <Button variant="outline" onClick={handleFullscreen}>
                <Icon name={fullscreen ? "Minimize" : "Maximize"} size={16} />
              </Button>
            </div>
          </div>

          <div ref={containerRef} className="relative bg-black aspect-video">
            {streamLoading ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-white text-xs">Loading stream...</p>
                </div>
              </div>
            ) : error ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
                <Icon name="AlertTriangle" size={24} className="text-red-500 mb-2" />
                <p className="text-red-500 font-medium text-sm">Stream Error</p>
                <p className="text-gray-300 text-xs mt-1">{error}</p>
                <Button onClick={refreshStream} className="mt-2 text-xs px-2 py-1">Retry</Button>
              </div>
            ) : (
              <>
                <img
                  src={streamUrl}
                  title="Live Stream"
                  className="w-full h-full object-contain"
                  onLoad={handleIframeLoad}
                  onError={handleIframeError}
                  key={streamUrl} // Force re-render when URL changes
                />
                {selectedCamera.isStop && (
                  <div className="absolute top-4 right-4 bg-red-600 bg-opacity-75 text-white px-3 py-1 rounded-md flex items-center">
                    <Icon name="CameraOff" size={16} className="mr-2" />
                    <span className="text-sm font-medium">Capture Stopped</span>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="p-4 border-t">
            <p className="text-sm text-gray-600">
              This is a live stream from {selectedCamera.name}.
              {selectedCamera.isStop
                ? " The stream is active but image capture is currently stopped."
                : " Faces are being captured and stored in the background."
              }
            </p>
          </div>
        </>
      ) : (
        <>
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold">Active Camera Feeds</h3>
          </div>

          <div className="p-4 space-y-8">

            {/* CHECK-IN CAMERAS */}
            {checkInCameras.length > 0 && (
              <section>
                <h4 className="text-md font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <Icon name="LogIn" size={16} className="text-green-500" />
                  Check-in Cameras
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {checkInCameras.map(camera => (
                    <div
                      key={camera.id}
                      onClick={() => handleCameraSelect(camera)}
                      className="border rounded-xl bg-white shadow-sm hover:shadow-md transition cursor-pointer flex flex-col justify-between"
                    >
                      {/* Header */}
                      <div className="p-4 border-b">
                        <div className="flex justify-between items-start gap-2">
                          <h5 className="font-semibold text-gray-800 truncate">
                            {camera.name}
                          </h5>

                          <div className="flex flex-wrap gap-1 justify-end">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getCameraTypeColor(camera.camera_type)}`}>
                              {getCameraTypeDisplay(camera.camera_type)}
                            </span>

                            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                              Active
                            </span>

                            {camera.isStop && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                                Capture Stopped
                              </span>
                            )}
                          </div>
                        </div>

                        <p className="text-sm text-gray-500 mt-2 line-clamp-2">
                          {camera.description}
                        </p>
                      </div>
                            
                      {/* Footer */}
                      <div className="p-4 flex justify-between items-center">
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          ID: {camera.camera_id}
                        </span>

                        <Button variant="outline" size="sm">
                          View Stream
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* CHECK-OUT CAMERAS */}
            {checkOutCameras.length > 0 && (
              <section>
                <h4 className="text-md font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <Icon name="LogOut" size={16} className="text-blue-500" />
                  Check-out Cameras
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {checkOutCameras.map(camera => (
                    <div
                      key={camera.id}
                      onClick={() => handleCameraSelect(camera)}
                      className="border rounded-xl bg-white shadow-sm hover:shadow-md transition cursor-pointer flex flex-col justify-between"
                    >
                      <div className="p-4 border-b">
                        <div className="flex justify-between items-start gap-2">
                          <h5 className="font-semibold text-gray-800 truncate">
                            {camera.name}
                          </h5>

                          <div className="flex flex-wrap gap-1 justify-end">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getCameraTypeColor(camera.camera_type)}`}>
                              {getCameraTypeDisplay(camera.camera_type)}
                            </span>

                            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                              Active
                            </span>

                            {camera.isStop && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                                Capture Stopped
                              </span>
                            )}
                          </div>
                        </div>

                        <p className="text-sm text-gray-500 mt-2 line-clamp-2">
                          {camera.description}
                        </p>
                      </div>

                      <div className="p-4 flex justify-between items-center">
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          ID: {camera.camera_id}
                        </span>

                        <Button variant="outline" size="sm">
                          View Stream
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* EMPTY STATE */}
            {checkInCameras.length === 0 && checkOutCameras.length === 0 && (
              <div className="text-center py-12">
                <Icon name="CameraOff" size={48} className="text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">No active cameras found</p>
                <p className="text-gray-400 text-sm mt-1">
                  Please activate cameras in Camera Settings
                </p>
              </div>
            )}
          </div>

        </>
      )}
    </div>
  );
};

export default DirectVideoStream;